/**
 * P&L Summary Report — Professional owner-facing report with AI forecasting.
 * 
 * Features:
 * - Summary/Detail/Forecast toggle tabs
 * - YTD banner with key metrics
 * - Chart with actual + projected data (distinct colors)
 * - Combined AI analysis + forecast in single action
 * - Professional PDF export with chart and AI included
 */

import { useCallback, useEffect, useState } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, LabelList, ReferenceLine, Cell,
} from 'recharts';
import {
  fetchPnlSummaryReport, fetchPnlDetailReport, fetchPnlAiSummary,
  fetchBacklogForecast, exportPnlSummaryCSV, exportPnlDetailCSV, exportPnlFullPdf,
} from '../../api/reports';
import { LoadingSpinner, EmptyState } from '../common';
import ReportShell, { DateRangeFilter } from './ReportShell';

function fmtCurrency(v) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(v || 0);
}

function fmtCurrencyShort(v) {
  const num = parseFloat(v) || 0;
  if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `$${(num / 1000).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
}

function fmtPct(v) {
  const n = parseFloat(v) || 0;
  return `${n.toFixed(1)}%`;
}

// Custom label for bar chart values
function BarValueLabel({ x, y, width, value }) {
  if (!value || value === 0) return null;
  return (
    <text
      x={x + width / 2}
      y={y - 5}
      fill="#374151"
      fontSize={10}
      textAnchor="middle"
    >
      {fmtCurrencyShort(value)}
    </text>
  );
}

export default function PnlSummaryReport() {
  const [viewMode, setViewMode] = useState('summary');
  const [summaryData, setSummaryData] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [ytdData, setYtdData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [groupBy, setGroupBy] = useState('month');
  const [exporting, setExporting] = useState(null);

  // Combined AI state - single button triggers both summary + forecast
  const [aiSummary, setAiSummary] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  const params = {
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    group_by: groupBy,
  };

  // Load filtered report data
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [summary, detail] = await Promise.all([
        fetchPnlSummaryReport(params),
        fetchPnlDetailReport(params),
      ]);
      setSummaryData(summary);
      setDetailData(detail);
    } catch {
      setSummaryData(null);
      setDetailData(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, groupBy]);

  // Load YTD data
  const loadYtd = useCallback(async () => {
    try {
      const year = new Date().getFullYear();
      const ytd = await fetchPnlSummaryReport({
        date_from: `${year}-01-01`,
        date_to: new Date().toISOString().split('T')[0],
        group_by: 'month',
      });
      setYtdData(ytd);
    } catch {
      setYtdData(null);
    }
  }, []);

  // Combined AI analysis - triggers BOTH summary AND forecast
  const loadAiAnalysis = useCallback(async () => {
    setAiLoading(true);
    setAiError(null);
    try {
      // Run both API calls in parallel
      const [summaryResult, forecastResult] = await Promise.all([
        fetchPnlAiSummary({
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          group_by: groupBy,
        }),
        fetchBacklogForecast(),
      ]);
      
      setAiSummary(summaryResult);
      
      if (forecastResult.error) {
        setAiError(forecastResult.error);
        setForecast(null);
      } else {
        setForecast(forecastResult);
      }
    } catch (err) {
      setAiError(err.message || 'Failed to generate analysis');
      setAiSummary(null);
      setForecast(null);
    } finally {
      setAiLoading(false);
    }
  }, [dateFrom, dateTo, groupBy]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadYtd(); }, [loadYtd]);

  // Export handlers
  async function handleExportCSV() {
    setExporting('csv');
    try {
      if (viewMode === 'detail') {
        await exportPnlDetailCSV(params);
      } else {
        await exportPnlSummaryCSV(params);
      }
    } finally {
      setExporting(null);
    }
  }

  async function handleExportPDF() {
    setExporting('pdf');
    try {
      await exportPnlFullPdf({
        view_mode: viewMode,
        date_from: dateFrom || null,
        date_to: dateTo || null,
        group_by: groupBy,
        ai_summary: aiSummary?.summary || '',
        forecast: forecast?.forecast || null,
      });
    } finally {
      setExporting(null);
    }
  }

  const summaryRows = summaryData?.rows || [];
  const summaryTotals = summaryData?.totals || {};
  const detailRows = detailData?.rows || [];
  const detailTotals = detailData?.totals || {};

  // YTD calculations
  const ytdTotals = ytdData?.totals || {};
  const ytdRevenue = parseFloat(ytdTotals.revenue) || 0;
  const ytdCost = parseFloat(ytdTotals.cost) || 0;
  const ytdProfit = ytdRevenue - ytdCost;
  const ytdProfitPct = ytdRevenue > 0 ? (ytdProfit / ytdRevenue) * 100 : 0;

  // Backlog from AI summary or forecast data_summary
  // IMPORTANT: Use the same source for both top bar and forecast tab
  const backlog = aiSummary?.backlog || forecast?.data_summary || {};
  const backlogJobs = backlog.job_count || backlog.active_job_count || 0;
  const backlogRevenue = parseFloat(backlog.total_revenue) || 0;
  const backlogProfit = parseFloat(backlog.total_projected_profit) || 0;

  // Chart data for Summary/Detail (actuals only)
  const chartDataActuals = summaryRows.map((r) => ({
    period: r.period,
    revenue: parseFloat(r.revenue) || 0,
    cost: parseFloat(r.cost) || 0,
    profit: parseFloat(r.margin) || 0,
    margin: parseFloat(r.margin_pct) || 0,
    isProjected: false,
  }));

  // Forecast data
  const forecastData = forecast?.forecast || {};
  const monthlyProjections = forecastData.monthly_projections || [];
  const jobEstimates = forecastData.job_estimates || [];
  const keyRisks = forecastData.key_risks || [];

  // Combined chart data for Forecast tab (actuals + projections)
  const ytdRows = ytdData?.rows || [];
  const chartDataCombined = [
    // YTD actual data
    ...ytdRows.map((r) => ({
      period: r.period,
      revenue: parseFloat(r.revenue) || 0,
      cost: parseFloat(r.cost) || 0,
      profit: parseFloat(r.margin) || 0,
      margin: parseFloat(r.margin_pct) || 0,
      isProjected: false,
    })),
    // Projected data from forecast
    ...monthlyProjections.map((m) => ({
      period: m.month,
      revenue: parseFloat(m.revenue) || 0,
      cost: parseFloat(m.revenue) - parseFloat(m.profit), // Derive cost
      profit: parseFloat(m.profit) || 0,
      margin: m.revenue > 0 ? (m.profit / m.revenue * 100) : 0,
      isProjected: true,
    })),
  ];

  // Find the last actual period for reference line
  const lastActualPeriod = ytdRows.length > 0 ? ytdRows[ytdRows.length - 1].period : null;

  // Check if AI analysis has been generated
  const hasAiAnalysis = aiSummary || forecast;

  return (
    <ReportShell
      title="P&L Summary"
      exporting={exporting}
      onExportCSV={handleExportCSV}
      onExportPDF={handleExportPDF}
      filters={
        <>
          <DateRangeFilter
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChangeFrom={setDateFrom}
            onChangeTo={setDateTo}
          />
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Group By</label>
            <select
              className="border border-gray-300 rounded-md px-2 py-1 text-sm"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
            >
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
              <option value="year">Year</option>
            </select>
          </div>
          <button
            onClick={load}
            className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 self-end"
          >
            Run
          </button>
        </>
      }
    >
      {/* YTD + Backlog Banner */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-lg shadow-md px-5 py-4 mb-4 text-white">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <YtdMetric label="YTD Revenue" value={fmtCurrency(ytdRevenue)} />
          <YtdMetric
            label="YTD Profit"
            value={fmtCurrency(ytdProfit)}
            color={ytdProfit >= 0 ? 'text-green-400' : 'text-red-400'}
          />
          <YtdMetric
            label="YTD Margin"
            value={fmtPct(ytdProfitPct)}
            color={ytdProfitPct >= 20 ? 'text-green-400' : 'text-yellow-400'}
          />
          <YtdMetric
            label="Backlog Jobs"
            value={backlogJobs}
            sublabel="active"
          />
          <YtdMetric
            label="Backlog Revenue"
            value={fmtCurrency(backlogRevenue)}
            sublabel="projected"
          />
          <YtdMetric
            label="Backlog Profit"
            value={fmtCurrency(backlogProfit)}
            color={backlogProfit >= 0 ? 'text-green-400' : 'text-red-400'}
            sublabel="projected"
          />
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('summary')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'summary'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Summary
          </button>
          <button
            onClick={() => setViewMode('detail')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'detail'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Detail
          </button>
          <button
            onClick={() => setViewMode('forecast')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1 ${
              viewMode === 'forecast'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <SparklesIcon className="w-3.5 h-3.5" />
            Forecast
          </button>
        </div>

        {/* Single Generate AI Analysis Button */}
        <button
          onClick={loadAiAnalysis}
          disabled={aiLoading}
          className={`px-4 py-1.5 text-sm font-medium rounded-lg flex items-center gap-2 transition-colors ${
            aiLoading
              ? 'bg-purple-200 text-purple-400 cursor-not-allowed'
              : hasAiAnalysis
                ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                : 'bg-purple-600 text-white hover:bg-purple-700'
          }`}
        >
          <SparklesIcon className="w-4 h-4" />
          {aiLoading ? 'Generating...' : hasAiAnalysis ? 'Refresh Analysis' : 'Generate AI Analysis'}
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><LoadingSpinner /></div>
      ) : viewMode === 'forecast' ? (
        /* ============ FORECAST VIEW ============ */
        <ForecastView
          ytdRows={ytdRows}
          chartDataCombined={chartDataCombined}
          lastActualPeriod={lastActualPeriod}
          monthlyProjections={monthlyProjections}
          jobEstimates={jobEstimates}
          keyRisks={keyRisks}
          forecastData={forecastData}
          forecast={forecast}
          aiSummary={aiSummary}
          aiLoading={aiLoading}
          aiError={aiError}
          onLoadAiAnalysis={loadAiAnalysis}
          ytdRevenue={ytdRevenue}
          ytdProfit={ytdProfit}
          backlogRevenue={backlogRevenue}
          backlogProfit={backlogProfit}
        />
      ) : (
        /* ============ SUMMARY / DETAIL VIEW ============ */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main Content - 2/3 width on large screens */}
          <div className="lg:col-span-2 space-y-4">
            {/* Chart - Always visible */}
            {chartDataActuals.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Revenue, Cost & Profit by Period
                </h3>
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={chartDataActuals} margin={{ top: 25, right: 30, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={fmtCurrencyShort} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} unit="%" />
                    <Tooltip
                      formatter={(value, name) => {
                        if (name === 'Margin %') return `${value.toFixed(1)}%`;
                        return fmtCurrency(value);
                      }}
                    />
                    <Legend />
                    <Bar yAxisId="left" dataKey="revenue" fill="#3b82f6" name="Revenue">
                      <LabelList dataKey="revenue" content={<BarValueLabel />} />
                    </Bar>
                    <Bar yAxisId="left" dataKey="cost" fill="#ef4444" name="Cost">
                      <LabelList dataKey="cost" content={<BarValueLabel />} />
                    </Bar>
                    <Bar yAxisId="left" dataKey="profit" fill="#10b981" name="Profit">
                      <LabelList dataKey="profit" content={<BarValueLabel />} />
                    </Bar>
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="margin"
                      stroke="#8b5cf6"
                      name="Margin %"
                      strokeWidth={2}
                      dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
                      label={({ x, y, value }) => (
                        <text x={x} y={y - 10} fill="#8b5cf6" fontSize={10} textAnchor="middle">
                          {value.toFixed(1)}%
                        </text>
                      )}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Data Table */}
            {viewMode === 'summary' ? (
              summaryRows.length === 0 ? (
                <EmptyState
                  title="No P&L data"
                  description="Complete and archive jobs to populate the P&L summary."
                />
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                        <th className="px-3 py-2">Period</th>
                        <th className="px-3 py-2 text-right">Jobs</th>
                        <th className="px-3 py-2 text-right">Revenue</th>
                        <th className="px-3 py-2 text-right">Cost</th>
                        <th className="px-3 py-2 text-right">Profit</th>
                        <th className="px-3 py-2 text-right">Margin</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {summaryRows.map((r) => (
                        <tr key={r.period} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium">{r.period}</td>
                          <td className="px-3 py-2 text-right">{r.job_count}</td>
                          <td className="px-3 py-2 text-right">{fmtCurrency(r.revenue)}</td>
                          <td className="px-3 py-2 text-right">{fmtCurrency(r.cost)}</td>
                          <td className="px-3 py-2 text-right">{fmtCurrency(r.margin)}</td>
                          <td className="px-3 py-2 text-right">
                            <span className={parseFloat(r.margin_pct) >= 20 ? 'text-green-600' : 'text-yellow-600'}>
                              {r.margin_pct}%
                            </span>
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-slate-800 text-white font-semibold">
                        <td className="px-3 py-2">TOTAL</td>
                        <td className="px-3 py-2 text-right">{summaryTotals.job_count}</td>
                        <td className="px-3 py-2 text-right">{fmtCurrency(summaryTotals.revenue)}</td>
                        <td className="px-3 py-2 text-right">{fmtCurrency(summaryTotals.cost)}</td>
                        <td className="px-3 py-2 text-right">{fmtCurrency(summaryTotals.margin)}</td>
                        <td className="px-3 py-2" />
                      </tr>
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              /* DETAIL VIEW */
              detailRows.length === 0 ? (
                <EmptyState
                  title="No job data"
                  description="Complete and archive jobs to see detailed P&L."
                />
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                        <th className="px-2 py-2">Job</th>
                        <th className="px-2 py-2">Date</th>
                        <th className="px-2 py-2">Customer</th>
                        <th className="px-2 py-2 text-right">Revenue</th>
                        <th className="px-2 py-2 text-right">Cost</th>
                        <th className="px-2 py-2 text-right">Profit</th>
                        <th className="px-2 py-2 text-right">Margin</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {detailRows.map((r) => (
                        <tr key={r.job_id} className="hover:bg-gray-50">
                          <td className="px-2 py-1.5 font-medium text-xs">{r.job_number}</td>
                          <td className="px-2 py-1.5 text-xs text-gray-600">{r.invoice_date || '-'}</td>
                          <td className="px-2 py-1.5 text-xs truncate max-w-[150px]">{r.customer_name}</td>
                          <td className="px-2 py-1.5 text-right text-xs">{fmtCurrency(r.revenue)}</td>
                          <td className="px-2 py-1.5 text-right text-xs">{fmtCurrency(r.cost)}</td>
                          <td className="px-2 py-1.5 text-right text-xs font-medium">
                            <span className={parseFloat(r.profit) >= 0 ? 'text-green-600' : 'text-red-600'}>
                              {fmtCurrency(r.profit)}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-right text-xs">
                            <span className={parseFloat(r.margin_pct) >= 20 ? 'text-green-600' : 'text-yellow-600'}>
                              {r.margin_pct}%
                            </span>
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-slate-800 text-white font-semibold">
                        <td className="px-2 py-2" colSpan={3}>
                          TOTAL ({detailTotals.job_count} jobs)
                        </td>
                        <td className="px-2 py-2 text-right">{fmtCurrency(detailTotals.revenue)}</td>
                        <td className="px-2 py-2 text-right">{fmtCurrency(detailTotals.cost)}</td>
                        <td className="px-2 py-2 text-right">{fmtCurrency(detailTotals.margin)}</td>
                        <td className="px-2 py-2 text-right">{detailTotals.avg_margin_pct}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>

          {/* AI Summary Sidebar - 1/3 width on large screens */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-gray-200 p-4 sticky top-4">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-3">
                <SparklesIcon className="w-4 h-4 text-purple-600" />
                AI Analysis & Outlook
              </h3>

              {aiLoading ? (
                <div className="flex flex-col items-center justify-center py-8 gap-2">
                  <LoadingSpinner size="sm" />
                  <p className="text-xs text-gray-500">Analyzing data & generating forecast...</p>
                </div>
              ) : aiError ? (
                <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
                  {aiError}
                </div>
              ) : aiSummary?.summary ? (
                <div className="text-sm text-gray-700 leading-relaxed">
                  {aiSummary.summary}
                </div>
              ) : (
                <div className="text-sm text-gray-500 italic py-4 text-center">
                  Click "Generate AI Analysis" to create a forecast with monthly projections.
                </div>
              )}

              {/* PDF Export Note */}
              <div className="mt-4 pt-3 border-t border-gray-100">
                <p className="text-[10px] text-gray-400 italic">
                  PDF export includes chart, data table, and AI analysis with projections.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </ReportShell>
  );
}

/**
 * Forecast View - Full page with combined actual + projected chart
 * 
 * IMPORTANT: Backlog Revenue/Profit cards use the SAME values as the top bar
 * (from get_backlog_metrics). The monthly projections table shows the AI's
 * per-month breakdown, but the totals should match the top bar.
 */
function ForecastView({
  ytdRows,
  chartDataCombined,
  lastActualPeriod,
  monthlyProjections,
  jobEstimates,
  keyRisks,
  forecastData,
  forecast,
  aiSummary,
  aiLoading,
  aiError,
  onLoadAiAnalysis,
  ytdRevenue,
  ytdProfit,
  backlogRevenue,  // Same value as top bar
  backlogProfit,   // Same value as top bar
}) {
  // Calculate margin from backlog values
  const backlogMarginPct = backlogRevenue > 0 ? (backlogProfit / backlogRevenue * 100) : 0;

  // Colors for bars
  const ACTUAL_REVENUE = '#3b82f6';
  const ACTUAL_PROFIT = '#10b981';
  const PROJECTED_REVENUE = '#93c5fd'; // Lighter blue
  const PROJECTED_PROFIT = '#6ee7b7';  // Lighter green

  const hasAnalysis = forecast || aiSummary;

  return (
    <div className="space-y-4">
      {/* Prompt to generate if no analysis */}
      {!hasAnalysis && !aiLoading && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6 text-center">
          <SparklesIcon className="w-8 h-8 text-purple-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">AI-Powered Revenue Forecast</h3>
          <p className="text-sm text-gray-600 mb-4">
            Generate intelligent predictions for job completion dates and monthly revenue projections
            based on historical performance and current backlog status.
          </p>
          <button
            onClick={onLoadAiAnalysis}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium"
          >
            Generate AI Analysis
          </button>
        </div>
      )}

      {/* Loading state */}
      {aiLoading && (
        <div className="bg-white rounded-lg border border-gray-200 p-10 text-center">
          <LoadingSpinner />
          <p className="text-sm text-gray-500 mt-3">Analyzing job metrics & generating forecast...</p>
          <p className="text-xs text-gray-400 mt-1">This may take a moment</p>
        </div>
      )}

      {/* Error state */}
      {aiError && !aiLoading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <p className="font-medium">Analysis Error</p>
          <p className="text-sm">{aiError}</p>
          <button
            onClick={onLoadAiAnalysis}
            className="mt-3 px-4 py-1 text-sm bg-red-100 rounded hover:bg-red-200"
          >
            Retry
          </button>
        </div>
      )}

      {/* Forecast Content */}
      {hasAnalysis && !aiLoading && (
        <>
          {/* Summary Cards - Use SAME backlog values as top bar */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryCard
              label="YTD Revenue"
              value={fmtCurrency(ytdRevenue)}
              sublabel="Actual"
              bgColor="bg-blue-50"
              borderColor="border-blue-200"
            />
            <SummaryCard
              label="Backlog Revenue"
              value={fmtCurrency(backlogRevenue)}
              sublabel="Total Active Jobs"
              bgColor="bg-blue-100"
              borderColor="border-blue-300"
              isProjected
            />
            <SummaryCard
              label="YTD Profit"
              value={fmtCurrency(ytdProfit)}
              sublabel="Actual"
              bgColor="bg-green-50"
              borderColor="border-green-200"
            />
            <SummaryCard
              label="Backlog Profit"
              value={fmtCurrency(backlogProfit)}
              sublabel={`${backlogMarginPct.toFixed(1)}% margin`}
              bgColor="bg-green-100"
              borderColor="border-green-300"
              isProjected
            />
          </div>

          {/* Combined Chart - Actuals + Projections */}
          {chartDataCombined.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">
                  Revenue & Profit — Actual + Projected
                </h3>
                <div className="flex items-center gap-4 text-xs">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded" style={{ backgroundColor: ACTUAL_REVENUE }}></span>
                    Actual
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded" style={{ backgroundColor: PROJECTED_REVENUE }}></span>
                    Projected
                  </span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={chartDataCombined} margin={{ top: 25, right: 30, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtCurrencyShort} />
                  <Tooltip
                    formatter={(value, name, props) => {
                      const label = props.payload.isProjected ? `${name} (Projected)` : name;
                      return [fmtCurrency(value), label];
                    }}
                    labelFormatter={(label, payload) => {
                      if (payload && payload[0]?.payload?.isProjected) {
                        return `${label} (Projected)`;
                      }
                      return label;
                    }}
                  />
                  <Legend />
                  {/* Reference line to mark where projections start */}
                  {lastActualPeriod && monthlyProjections.length > 0 && (
                    <ReferenceLine
                      x={lastActualPeriod}
                      stroke="#9333ea"
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      label={{
                        value: 'Projections →',
                        position: 'top',
                        fill: '#9333ea',
                        fontSize: 10,
                      }}
                    />
                  )}
                  <Bar dataKey="revenue" name="Revenue">
                    {chartDataCombined.map((entry, index) => (
                      <Cell
                        key={`revenue-${index}`}
                        fill={entry.isProjected ? PROJECTED_REVENUE : ACTUAL_REVENUE}
                      />
                    ))}
                  </Bar>
                  <Bar dataKey="profit" name="Profit">
                    {chartDataCombined.map((entry, index) => (
                      <Cell
                        key={`profit-${index}`}
                        fill={entry.isProjected ? PROJECTED_PROFIT : ACTUAL_PROFIT}
                      />
                    ))}
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Executive Summary + Monthly Table + Risks */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Executive Summary & Risks */}
            <div className="lg:col-span-1 space-y-4">
              {/* AI Summary */}
              {aiSummary?.summary && (
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                    <SparklesIcon className="w-4 h-4 text-purple-600" />
                    Analysis & Outlook
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {aiSummary.summary}
                  </p>
                </div>
              )}

              {/* Executive Summary from forecast */}
              {forecastData.executive_summary && !aiSummary?.summary && (
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                    <SparklesIcon className="w-4 h-4 text-purple-600" />
                    AI Analysis
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {forecastData.executive_summary}
                  </p>
                </div>
              )}

              {/* Key Risks */}
              {keyRisks.length > 0 && (
                <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
                  <h4 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-1">
                    <WarningIcon className="w-4 h-4" />
                    Key Risks
                  </h4>
                  <ul className="space-y-2">
                    {keyRisks.map((risk, i) => (
                      <li key={i} className="text-sm text-amber-900 flex items-start gap-2">
                        <span className="text-amber-500 mt-0.5">•</span>
                        {risk}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Monthly Projections Table */}
            <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-4">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">
                AI Monthly Projections
                <span className="ml-2 text-xs font-normal text-gray-500">
                  (estimated completion schedule)
                </span>
              </h4>
              {monthlyProjections.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-purple-50 text-purple-800 text-xs uppercase">
                      <th className="px-3 py-2 text-left">Month</th>
                      <th className="px-3 py-2 text-right">Jobs</th>
                      <th className="px-3 py-2 text-right">Revenue</th>
                      <th className="px-3 py-2 text-right">Profit</th>
                      <th className="px-3 py-2 text-right">Margin</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {monthlyProjections.map((m) => {
                      const margin = m.revenue > 0 ? (m.profit / m.revenue * 100) : 0;
                      return (
                        <tr key={m.month} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium">{m.month}</td>
                          <td className="px-3 py-2 text-right">{m.jobs_completing}</td>
                          <td className="px-3 py-2 text-right">{fmtCurrency(m.revenue)}</td>
                          <td className="px-3 py-2 text-right text-green-600 font-medium">{fmtCurrency(m.profit)}</td>
                          <td className="px-3 py-2 text-right">{margin.toFixed(1)}%</td>
                        </tr>
                      );
                    })}
                    {/* Show backlog totals as the TOTAL row - same as top bar */}
                    <tr className="bg-purple-100 font-semibold">
                      <td className="px-3 py-2">TOTAL BACKLOG</td>
                      <td className="px-3 py-2 text-right">
                        {forecast?.data_summary?.active_job_count || '-'}
                      </td>
                      <td className="px-3 py-2 text-right">{fmtCurrency(backlogRevenue)}</td>
                      <td className="px-3 py-2 text-right text-green-700">{fmtCurrency(backlogProfit)}</td>
                      <td className="px-3 py-2 text-right">
                        {backlogMarginPct.toFixed(1)}%
                      </td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-gray-500 italic">No monthly projections available.</p>
              )}
            </div>
          </div>

          {/* Job Estimates */}
          {jobEstimates.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">Job Completion Estimates</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-600 text-xs uppercase">
                      <th className="px-3 py-2 text-left">Job #</th>
                      <th className="px-3 py-2 text-left">Est. Completion</th>
                      <th className="px-3 py-2 text-left">Confidence</th>
                      <th className="px-3 py-2 text-left">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {jobEstimates.map((j) => (
                      <tr key={j.job_number} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium">{j.job_number}</td>
                        <td className="px-3 py-2">{j.estimated_completion_month}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            j.confidence === 'high' ? 'bg-green-100 text-green-700' :
                            j.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {j.confidence}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-600 text-xs">{j.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* PDF Note */}
          <p className="text-[10px] text-gray-400 italic text-center">
            PDF export will include this forecast chart and analysis along with the selected view (Summary or Detail).
          </p>
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, sublabel, bgColor, borderColor, isProjected }) {
  return (
    <div className={`${bgColor} border ${borderColor} rounded-lg p-4 ${isProjected ? 'border-dashed' : ''}`}>
      <dt className="text-xs text-gray-600 uppercase tracking-wider">{label}</dt>
      <dd className="text-xl font-bold text-gray-900 mt-1">{value}</dd>
      {sublabel && <dd className="text-[10px] text-gray-500 mt-0.5">{sublabel}</dd>}
    </div>
  );
}

function YtdMetric({ label, value, color = 'text-white', sublabel }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-gray-400">{label}</dt>
      <dd className={`text-lg font-bold ${color}`}>{value}</dd>
      {sublabel && <dd className="text-[9px] text-gray-500">{sublabel}</dd>}
    </div>
  );
}

function SparklesIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
      />
    </svg>
  );
}

function WarningIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
      />
    </svg>
  );
}
